<form class="pb-4" [formGroup]="form" (ngSubmit)="onSubmit()">

  <!-- Primary Information Section -->
  <div class="form-section">
    <div class="collapsible-section" [class.is-expanded]="primaryInfoExpanded">
      <div class="collapsible-header" role="button" tabindex="0"
        (click)="primaryInfoExpanded = !primaryInfoExpanded"
        (keydown.enter)="primaryInfoExpanded = !primaryInfoExpanded"
        (keydown.space)="$event.preventDefault(); primaryInfoExpanded = !primaryInfoExpanded"
        [attr.aria-expanded]="primaryInfoExpanded">
        <div class="flex items-center justify-between w-full">
          <span class="collapsible-header-title">Primary Information</span>
          <div class="flex items-center gap-2">
            <p-button
              *ngIf="mode !== 'create'"
              label="View History"
              [text]="true"
              severity="secondary"
              (click)="$event.stopPropagation(); openHistoryModal()">
            </p-button>
            <span class="collapsible-chevron" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" stroke-width="1.5"
                  stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
          </div>
        </div>
      </div>
      <div class="collapsible-content">
        <div class="collapsible-content-inner">
          <div class="flex gap-2 items-start">
            <div class="flex-1">
              <app-dynamic-form
                [gridSize]="6"
                [viewMode]="mode === 'view'"
                [formGroup]="form"
                [data]="primaryFields"
                (fieldChanged)="onFormFieldChanged($event)"
                (searchQuery)="onSearchQuery($event)"
                (fileUploadRequested)="onFileUploadRequested($event, 'form')"
                (fileDownloadRequested)="onFileDownloadRequested($event)">
              </app-dynamic-form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Tabs for dynamic content -->
  <p-tabs *ngIf="dynamicFormTabs.length > 0" [value]="activeTabIndex.toString()">
    <p-tablist>
      <p-tab *ngFor="let tab of dynamicFormTabs" [value]="tab.value.toString()">
        <p class="font-normal text-sm">{{ tab.title }}</p>
      </p-tab>
    </p-tablist>
    <p-tabpanels>
      <p-tabpanel *ngFor="let tab of dynamicFormTabs" [value]="tab.value.toString()">
        <ng-container>
          <div *ngFor="let sub_tab of tab.sub_tabs" class="mb-6">
            <ng-container *ngIf="sub_tab.content.length > 0 || sub_tab.sub_type === 'TABLE'">
              <h5 *ngIf="sub_tab.show_heading === true" class="mb-4 font-semibold">{{ sub_tab.name }}</h5>
              
              <ng-container *ngIf="sub_tab.sub_type !== 'TABLE'; else tableDynamicForm">
                <app-dynamic-form 
                  [gridSize]="6"
                  [viewMode]="mode === 'view'" 
                  [formGroup]="form"
                  [data]="sub_tab.content"
                  (fieldChanged)="onFormFieldChanged($event)"
                  (fileUploadRequested)="onFileUploadRequested($event, 'form')"
                  (searchQuery)="onSearchQuery($event)"
                  (fileDownloadRequested)="onFileDownloadRequested($event)"
                  (buttonClicked)="onButtonClicked($event)">
                </app-dynamic-form>
              </ng-container>

              <ng-template #tableDynamicForm>
                <!-- Asset Items Table (only visible in create mode) -->
                <ng-container *ngIf="sub_tab.key === ASSET_ITEMS_TABLE_KEY">
                  <div *ngIf="mode === 'create'" class="items-table-section">
                    <div class="table-header">
                      <h4 *ngIf="sub_tab.show_heading === true">{{ sub_tab.name }}</h4>
                    </div>
                    <app-item-dynamic-form
                      [viewMode]="false"
                      [formGroup]="form"
                      [tableKey]="sub_tab.key"
                      [tableArray]="getTableFormArray(sub_tab.key)"
                      [data]="sub_tab.content"
                      [showAddRemoveButtons]="STANDALONE_MODE"
                      [showAddButton]="STANDALONE_MODE"
                      [showDuplicateRowButton]="false"
                      [showBulkActionButtons]="false"
                      (searchQuery)="onItemSearchQuery($event, sub_tab)"
                      (fieldChanged)="onItemFieldChanged($event)"
                      (rowDuplicated)="onRowDuplicated($event, sub_tab)"
                      (fileUploadRequested)="onFileUploadRequested($event, 'table', sub_tab.key)"
                      (fileDownloadRequested)="onFileDownloadRequested($event)">
                    </app-item-dynamic-form>
                  </div>
                  
                </ng-container>

                <!-- Other tables (like maintenance_schedule) - visible in all modes -->
                <ng-container *ngIf="sub_tab.key !== ASSET_ITEMS_TABLE_KEY">
                  <div class="items-table-section">
                    <div class="table-header">
                      <h4 *ngIf="sub_tab.show_heading === true">{{ sub_tab.name }}</h4>
                    </div>
                    <app-item-dynamic-form 
                      [viewMode]="mode === 'view'" 
                      [formGroup]="form" 
                      [tableKey]="sub_tab.key"
                      [tableArray]="getTableFormArray(sub_tab.key)" 
                      [data]="sub_tab.content"
                      [showAddRemoveButtons]="mode !== 'view'"
                      [showAddButton]="mode !== 'view'"
                      [showDuplicateRowButton]="mode !== 'view'"
                      [showBulkActionButtons]="mode !== 'view'"
                      (searchQuery)="onItemSearchQuery($event, sub_tab)"
                      (fieldChanged)="onItemFieldChanged($event)"
                      (rowDuplicated)="onRowDuplicated($event, sub_tab)"
                      (fileUploadRequested)="onFileUploadRequested($event, 'table', sub_tab.key)"
                      (fileDownloadRequested)="onFileDownloadRequested($event)">
                    </app-item-dynamic-form>
                  </div>
                </ng-container>
              </ng-template>
            </ng-container>
          </div>
        </ng-container>
      </p-tabpanel>
    </p-tabpanels>
  </p-tabs>

  <!-- Save/Submit Button -->
  <div class="save-container" *ngIf="mode !== 'view'">
    <div class="flex items-center gap-3">
      <p-button 
        type="button" 
        label="Cancel" 
        styleClass="p-button-outlined p-button-secondary"
        (onClick)="goBack()" />
      <p-button 
        type="submit" 
        [label]="mode === 'create' ? 'Create Assets' : 'Update Asset'" 
        [disabled]="isLoading"
        [loading]="isSubmitting" />
    </div>
  </div>

</form>

<!-- QR Print Modal -->
<app-qr-print-modal
  [(visible)]="qrPrintModalVisible"
  [assetId]="assetId"
  [assetData]="assetData"
  (printed)="onQRPrinted($event)">
</app-qr-print-modal>

<!-- Asset History Modal -->
<app-asset-history-modal
  [(visible)]="historyModalVisible"
  [assetId]="assetId">
</app-asset-history-modal>

<!-- Loading state -->
<div *ngIf="isLoading" class="nf-overlay">
  <svg class="nf-spinner" viewBox="0 0 50 50">
    <circle class="nf-spinner__track" cx="25" cy="25" r="20"></circle>
    <circle class="nf-spinner__arc" cx="25" cy="25" r="20"></circle>
  </svg>
</div>
